import React, { useState, useEffect } from 'react';
import { Calendar, Plus, Minus, Eye, AlertCircle, Loader, Activity, Thermometer, Heart, Zap } from 'lucide-react';
import { patientHistoryService } from '../../../../services/patientHistoryService';
import { patientService } from '../../../patient/services/patientService';

const HistoryContent = ({ patientId }) => {
  // State management
  const [patient, setPatient] = useState(null);
  const [appointmentHistory, setAppointmentHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedAppointments, setExpandedAppointments] = useState(new Set([0])); // First appointment expanded by default
  const [activeTabsByAppointment, setActiveTabsByAppointment] = useState({});
  const [historyStats, setHistoryStats] = useState(null);

  // Load patient data and appointment history on component mount
  useEffect(() => {
    if (patientId) {
      loadPatientAndHistory();
    }
  }, [patientId]);

  const loadPatientAndHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log(`🔄 Loading patient data for patient ID: ${patientId}`);
      
      // First, get the patient data to extract the numeric ID
      const patientData = await patientService.getPatientByPatientId(patientId);
      console.log('👤 Patient data loaded:', patientData);
      
      setPatient(patientData);
      
      // Now load appointment history using the numeric patient ID
      const numericPatientId = patientData.id;
      console.log(`🔄 Loading appointment history for numeric patient ID: ${numericPatientId}`);
      
      const historyData = await patientHistoryService.getPatientHistory(numericPatientId);
      
      console.log('📊 History data received:', historyData);
      
      if (historyData.success && historyData.appointments) {
        setAppointmentHistory(historyData.appointments);
        setHistoryStats(historyData.summary);
        
        // Initialize active tabs for each appointment
        const initialTabs = {};
        historyData.appointments.forEach((_, index) => {
          initialTabs[index] = 'medications';
        });
        setActiveTabsByAppointment(initialTabs);
        
        console.log(`✅ Loaded ${historyData.appointments.length} appointments for patient ${patientData.first_name} ${patientData.last_name}`);
      } else {
        throw new Error('Invalid response format from server');
      }
      
    } catch (err) {
      console.error('❌ Error loading patient data or appointment history:', err);
      setError(err.message || 'Failed to load appointment history');
      // Set empty state on error
      setAppointmentHistory([]);
      setHistoryStats(null);
      setPatient(null);
    } finally {
      setLoading(false);
    }
  };

  const toggleAppointmentExpanded = (index) => {
    const newExpanded = new Set(expandedAppointments);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedAppointments(newExpanded);
  };

  const setActiveTab = (appointmentIndex, tab) => {
    setActiveTabsByAppointment(prev => ({
      ...prev,
      [appointmentIndex]: tab
    }));
  };

  // Helper component for rendering vital signs
  const VitalField = ({ label, value, unit, icon: Icon }) => (
    <div className="text-center">
      <label className="block text-xs font-medium text-gray-600 mb-1">
        {Icon && <Icon className="h-3 w-3 inline mr-1" />}
        {label}
      </label>
      <div className="relative">
        <div className="bg-gray-50 border border-gray-300 rounded px-2 py-1 text-sm font-medium">
          {value || '--'}
        </div>
        {unit && value && (
          <div className="absolute inset-y-0 right-0 flex items-center">
            <div className="h-full w-px bg-gray-300 mx-1"></div>
            <span className="text-xs text-gray-500 font-medium pr-2">{unit}</span>
          </div>
        )}
      </div>
    </div>
  );

  const renderVitalsGrid = (vitals) => {
    if (!vitals) {
      return (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span className="text-sm">No vital signs recorded for this appointment</span>
        </div>
      );
    }

    const formattedVitals = patientHistoryService.formatVitals(vitals);
    
    return (
      <div className="grid gap-3 mb-4" style={{gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 0.8fr 1.4fr 1fr 1fr'}}>
        <VitalField 
          label="Temperature" 
          value={formattedVitals.temperatureValue} 
          unit="°C"
          icon={Thermometer}
        />
        <VitalField 
          label="Pulse Rate" 
          value={formattedVitals.pulseRateValue} 
          unit="/min"
          icon={Heart}
        />
        <VitalField 
          label="SpO2" 
          value={formattedVitals.spo2Value} 
          unit="%"
          icon={Activity}
        />
        <VitalField 
          label="Height" 
          value={formattedVitals.heightValue} 
          unit={vitals.height_unit || "cm"} 
        />
        <VitalField 
          label="Weight" 
          value={formattedVitals.weightValue} 
          unit={vitals.weight_unit || "kg"} 
        />
        <VitalField 
          label="BMI" 
          value={formattedVitals.bmiValue} 
          unit="" 
        />
        <VitalField 
          label="Blood Pressure" 
          value={formattedVitals.systolicBp && formattedVitals.diastolicBp ? 
                `${formattedVitals.systolicBp}/${formattedVitals.diastolicBp}` : null} 
          unit="mmHg" 
        />
        <VitalField 
          label="Respiratory Rate" 
          value={formattedVitals.respiratoryRateValue} 
          unit="/min" 
        />
        <VitalField 
          label="Glucose" 
          value={formattedVitals.glucoseValue} 
          unit="mg/dL" 
        />
      </div>
    );
  };

  const renderNotes = (notes) => (
    <div className="p-4 space-y-4">
      {/* Patient Notes */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Patient Notes</label>
        <textarea
          readOnly
          value={notes.patient.length > 0 ? notes.patient[0].content : "No patient notes recorded for this appointment"}
          className="w-full h-20 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-700 resize-none"
        />
      </div>

      {/* Clinician Notes */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Clinician Notes</label>
        <textarea
          readOnly
          value={notes.clinician.length > 0 ? notes.clinician[0].content : "No clinician notes recorded for this appointment"}
          className="w-full h-20 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-700 resize-none"
        />
      </div>

      {/* Doctor Notes */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-2">Doctor Notes</label>
        <textarea
          readOnly
          value={notes.doctor.length > 0 ? notes.doctor[0].content : "No doctor notes recorded for this appointment"}
          className="w-full h-20 px-3 py-2 bg-gray-50 border border-gray-300 rounded-md text-sm text-gray-700 resize-none"
        />
      </div>
    </div>
  );

  const renderMedications = (medications) => {
    if (!medications || medications.length === 0) {
      return (
        <div className="flex items-center justify-center py-8 text-gray-500">
          <AlertCircle className="h-5 w-5 mr-2" />
          <span className="text-sm">No medications prescribed during this appointment</span>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-3 gap-4">
        {medications.map((medication, index) => (
          <div key={medication.id || index} className="bg-gray-50 border border-gray-200 rounded p-3">
            <div className="font-medium text-gray-900 text-sm">
              {medication.medication_name} {medication.dosage}
            </div>
            <div className="text-xs text-gray-600 mt-1">
              {medication.frequency} | {medication.start_date} | {medication.indication}
            </div>
            {medication.prescribed_by_doctor_name && (
              <div className="text-xs text-gray-500 mt-1">
                Prescribed by: {medication.prescribed_by_doctor_name}
              </div>
            )}
            <div className={`inline-block px-2 py-1 text-xs rounded-full mt-2 ${
              medication.status === 'Active' ? 'bg-green-100 text-green-800' :
              medication.status === 'Discontinued' ? 'bg-red-100 text-red-800' :
              medication.status === 'Completed' ? 'bg-blue-100 text-blue-800' :
              'bg-gray-100 text-gray-800'
            }`}>
              {medication.status}
            </div>
          </div>
        ))}
      </div>
    );
  };

  const renderEquipmentButtons = (vitals) => {
    const equipment = ['Spirometry', 'Stethoscope', 'ECG', 'Otoscope'];
    
    return (
      <div className="grid grid-cols-4 gap-4">
        {equipment.map((equipmentName) => {
          const hasData = vitals && patientHistoryService.hasEquipmentData(vitals, equipmentName.toLowerCase());
          
          return (
            <div key={equipmentName} className="text-center">
              <label className="block text-xs font-medium text-gray-600 mb-1">{equipmentName}</label>
              <button 
                className={`text-xs px-3 py-1 rounded transition-all duration-200 flex items-center justify-center space-x-1 w-20 mx-auto ${
                  hasData 
                    ? 'bg-white border border-blue-500 text-blue-500 hover:bg-blue-500 hover:text-white'
                    : 'bg-gray-100 border border-gray-300 text-gray-400 cursor-not-allowed'
                }`}
                disabled={!hasData}
                title={hasData ? `View ${equipmentName} data` : `No ${equipmentName} data available`}
              >
                <Eye className="h-3 w-3" />
                <span>View</span>
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  // Show loading state
  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
          <div className="text-gray-500">Loading appointment history...</div>
          {patientId && (
            <div className="text-sm text-gray-400 mt-2">Patient ID: {patientId}</div>
          )}
        </div>
      </div>
    );
  }

  // Show error state
  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center max-w-md">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <div className="text-red-600 font-medium mb-2">Error Loading History</div>
          <div className="text-gray-600 text-sm mb-4">{error}</div>
          <button 
            onClick={loadPatientAndHistory}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  // Show empty state
  if (appointmentHistory.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Calendar className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <div className="text-gray-500 font-medium mb-2">No Appointment History Found</div>
          <div className="text-gray-400 text-sm">This patient has no completed appointments on record.</div>
          {historyStats && (
            <div className="text-xs text-gray-400 mt-2">
              Total appointments: {historyStats.total_appointments} | 
              Completed: {historyStats.completed_appointments}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* History Summary Stats */}
      {historyStats && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-sm">
                <span className="font-medium text-blue-900">Total Appointments:</span>
                <span className="ml-1 text-blue-700">{historyStats.total_appointments}</span>
              </div>
              <div className="text-sm">
                <span className="font-medium text-blue-900">Completed:</span>
                <span className="ml-1 text-blue-700">{historyStats.completed_appointments}</span>
              </div>
              <div className="text-sm">
                <span className="font-medium text-blue-900">With Vitals:</span>
                <span className="ml-1 text-blue-700">{historyStats.appointments_with_vitals}</span>
              </div>
            </div>
            <div className="text-sm text-blue-600">
              Completion Rate: {Math.round(historyStats.completion_rate)}%
            </div>
          </div>
        </div>
      )}

      {/* Appointment History */}
      {appointmentHistory.map((appointment, index) => {
        const isExpanded = expandedAppointments.has(index);
        const activeTab = activeTabsByAppointment[index] || 'medications';
        const isCurrentAppointment = index === 0; // Most recent appointment
        
        // All headers should have blue background
        const headerColorClass = 'bg-blue-600';

        const appointmentDate = patientHistoryService.formatAppointmentDate(appointment.booking.appointment_date);
        const appointmentTime = patientHistoryService.formatAppointmentTime(appointment.booking.appointment_time);

        return (
          <div key={appointment.booking.id} className="bg-white border border-gray-200 rounded-lg shadow-sm">
            <div className={`${headerColorClass} text-white px-4 py-1.5 rounded-t-lg`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5" />
                  <h4 className="font-semibold">{appointmentDate}</h4>
                  {appointmentTime && (
                    <span className="text-sm text-gray-200">at {appointmentTime}</span>
                  )}
                  {!isCurrentAppointment && (
                    <span className="text-sm text-gray-300">(Previous Visit)</span>
                  )}
                  {appointment.booking.appointment_type && (
                    <span className="text-sm text-gray-300">
                      - {appointment.booking.appointment_type}
                    </span>
                  )}
                </div>
                <button
                  type="button"
                  onClick={() => toggleAppointmentExpanded(index)}
                  className="bg-white hover:bg-gray-50 text-gray-600 rounded-lg w-6 h-6 flex items-center justify-center transition-colors border border-gray-200"
                >
                  {isExpanded ? (
                    <Minus className="h-3 w-3" />
                  ) : (
                    <Plus className="h-3 w-3" />
                  )}
                </button>
              </div>
            </div>
            
            {isExpanded && (
              <div className="p-4 space-y-6">
                {/* Results Section */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="bg-gray-100 px-4 py-1.5 rounded-t-lg border-b border-gray-200">
                    <h4 className="font-semibold text-blue-600">Results</h4>
                  </div>
                  <div className="p-4">
                    {renderVitalsGrid(appointment.vitals)}
                    
                    {/* Medical Equipment Row */}
                    {renderEquipmentButtons(appointment.vitals)}
                  </div>
                </div>

                {/* Notes Section */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="bg-gray-100 px-4 py-1.5 rounded-t-lg border-b border-gray-200">
                    <h4 className="font-semibold text-blue-600">Notes</h4>
                  </div>
                  {renderNotes(appointment.notes)}
                </div>

                {/* Bottom Tabs Section */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg">
                  <div className="border-b border-gray-200">
                    <div className="flex">
                      {['medications', 'pathology', 'imaging', 'referrals'].map((tab) => (
                        <button 
                          key={tab}
                          onClick={() => setActiveTab(index, tab)}
                          className={`px-4 py-1.5 text-sm font-medium transition-colors capitalize ${
                            activeTab === tab 
                              ? 'text-blue-600 border-b-2 border-blue-600 bg-blue-50' 
                              : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                          }`}
                        >
                          {tab}
                          {tab === 'medications' && appointment.medications?.length > 0 && (
                            <span className="ml-1 bg-blue-100 text-blue-800 text-xs px-1.5 py-0.5 rounded-full">
                              {appointment.medications.length}
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="p-4">
                    {/* Medications Tab */}
                    {activeTab === 'medications' && renderMedications(appointment.medications)}

                    {/* Pathology, Imaging, Referrals - Placeholder for future implementation */}
                    {activeTab !== 'medications' && (
                      <div className="flex items-center justify-center py-8 text-gray-500">
                        <AlertCircle className="h-5 w-5 mr-2" />
                        <span className="text-sm">
                          {activeTab.charAt(0).toUpperCase() + activeTab.slice(1)} data will be available soon
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default HistoryContent;